import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import { Inject, Injectable, InjectionToken, Optional } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { delay } from "rxjs/operators";

/**
 * `Mockup` data object's injection token.
 */
export const MOCKUP = new InjectionToken<Mockup>("mockup");

/**
 * Supported HTTP methods
 */
export enum HttpMethod {
  GET = "GET",
  PATCH = "PATCH",
  POST = "POST",
  PUT = "PUT",
  DELETE = "DELETE",
}

/**
 * Supported HTTP status codes
 */
export enum HttpStatusCode {
  Success = 200,
  BadRequest = 400,
  Unauthorized = 401,
  NotFound = 404,
  MethodNotAllowed = 422,
  ServerError = 500,
}

/**
 * The request parameters represented as a key-value object
 */
export type Params = { [param: string]: string };

/**
 * Mockup object as passed to `SelfServiceMockupInterceptor`
 */
export interface Mockup {
  /**
   * JSON schema path
   */
  $schema?: string;
  /**
   * Delay response time in milliseconds. Default is `500`;
   */
  delay: number;
  /**
   * Key-value object representing the paths to intercept
   */
  paths: {
    /**
     * Specfic path to intercept
     */
    [path: string]: {
      /**
       * The method to intercept along with a list of data to filter and return as response
       */
      [method in HttpMethod]?: Array<Record<string, unknown>>;
    };
  };
}

/**
 * Intercepts requests made to the Self-Service API and returns mockup data as response.
 *
 * The mocked data to return is declared in the `self-service.mockup.json` file of the package root directory.
 *
 * When new data has been added, make sure the package is rebuilt for the changes to take place.
 */
@Injectable()
export class SelfServiceMockupInterceptor implements HttpInterceptor {
  constructor(@Optional() @Inject(MOCKUP) private mockup: Mockup) {
    // Throw an error if initialized without a mockup object provided
    if (!this.mockup)
      throw new Error("Could not initialize 'SelfServiceMockupInterceptor'. No mockup object was passed to the constructor.");
    // Set the default response time to 500 milliseconds
    if (this.mockup.delay == null) this.mockup.delay = 500;
  }

  /**
   * Intercept HTTP request and return appropriate HTTP response
   * @param request HTTP request object
   * @param next HTTP request handler
   */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const matchedUrlPath = Object.keys(this.mockup.paths).find((path) => this.matchUrlPath(path, request.url));

    if (!matchedUrlPath) return next.handle(request);
    const params = this.parseParams(matchedUrlPath, request);
    if (!this.mockup.paths[matchedUrlPath][request.method as HttpMethod]) {
      console.warn(
        `Could not intercept the request '${request.method} ${request.url}'. No mockup data exist for this path and method.`
      );
      return this.httpResponse(HttpStatusCode.MethodNotAllowed);
    }
    // Filter out objects in the mockup array that matches the parameters provided in the request URL
    // and remove all properties with an underscore from the response body
    const body = (this.mockup.paths[matchedUrlPath][request.method as HttpMethod] || [])
      .filter(this.matchIdentifier(params))
      .map(this.removeUnderscoreProperties);

    if (body.length)
      if (body.length === 1 && body[0].$array !== true)
        // Return an object in case only a single item match with the provided path and parameters
        // and the $array custom property has not been defined
        return this.httpResponse(HttpStatusCode.Success, body.map(this.removeDollarProperties).map(this.formatPrimitiveValue)[0]);
      else return this.httpResponse(HttpStatusCode.Success, body.map(this.removeDollarProperties).map(this.formatPrimitiveValue));

    return this.httpResponse(HttpStatusCode.NotFound);
  }

  /**
   * Simulate HTTP response
   * @param status HTTP response status code
   * @param body HTTP response body
   */
  private httpResponse(status: number, body?: unknown) {
    if (status >= 200 && status <= 300)
      return of(new HttpResponse({ status, body })).pipe(delay(this.mockup.delay)) as unknown as Observable<HttpEvent<unknown>>;
    else {
      return throwError(
        new HttpErrorResponse({
          error: `Error ${status}`,
          status,
        })
      ).pipe(delay(this.mockup.delay)) as unknown as Observable<HttpEvent<unknown>>;
    }
  }

  /**
   * Removes properties with an underscore from response body
   * @param body The body object to remove the properties from
   */
  private removeUnderscoreProperties(body: Record<string, unknown>) {
    return Object.fromEntries(Object.entries(body).filter(([key, _]) => !key.startsWith("_")));
  }

  /**
   * Removes properties with an dollar symbol from response body
   * @param body The body object to remove the properties from
   */
  private removeDollarProperties(body: Record<string, unknown>) {
    return Object.fromEntries(Object.entries(body).filter(([key, _]) => !key.startsWith("$") || key.startsWith("$value")));
  }

  /**
   * Formats body to return primitive values (in case `$value` property is present)
   * @param body The body object to remove the properties from
   */
  private formatPrimitiveValue(body: Record<string, unknown>) {
    if (body["$value"]) return body["$value"];
    return body;
  }

  /**
   * Match provided resource identifiers or query parameters against data items in `mockup` array. Will match data items which does not contain properties that might be specified in `params`.
   * @param params Parameters to match
   */
  private matchIdentifier(params: Params = {}) {
    return (item: Record<string, unknown> = {}) =>
      Object.entries(params)
        // Match wildcard properties (*) against any value
        // Testing for loose equality to match numeric values cast as strings, i.e. "123"
        .reduce((match, [key, value]) => (!item[key] || item[key] == value || item[key] === "*" ? match && true : false), true);
  }

  /**
   * Compares request URL with specified path matcher string
   * @param pathMatcher path matcher pattern to compare to the url
   * @param url url for the current request.
   */
  private matchUrlPath(pathMatcher: string, url: string) {
    const matcherSegments = pathMatcher.split("/");
    const urlSegments = new URL(url, "https://localhost:4200").pathname.split("/");
    // Do not match URL's that have more segments than declared in pathMatcher
    if (matcherSegments.length < urlSegments.length) return false;
    // Do not match if any of the URL segments does not match the segments in pathMatcher
    for (let index = 0; index < matcherSegments.length; index++) {
      if (!matcherSegments[index].startsWith(":")) if (matcherSegments[index] !== urlSegments[index]) return false;
    }
    return true;
  }

  /**
   * Takes an URL and parse it against a matching string. Returns an object with the resource name or query parameter name as key and resource identifier query parameter value as value.
   * @param pathMatcher path matcher pattern to compare to the url
   * @param url url for the request
   *
   * **Example**
   *
   * This input:
   *
   * * path: `component/:componentId`
   * * url: `component/123&queryParam=abc`
   *
   * Will give this output:
   *
   * ```
   * { _componentId: "123", _queryParam: "abc" }
   * ```
   */
  protected parseParams(pathMatcher: string, request: HttpRequest<unknown>): Params {
    const urlSegments = new URL(request.url, "https://localhost:4200").pathname
      .split("/")
      .map((path, index) => [new URL(pathMatcher, "https://localhost:4200").pathname.split("/")[index].replace(":", "_"), path])
      .filter(([key, value]) => key !== value);
    const urlSearchParams = Array.from<[string, string]>(
      new URLSearchParams(new URL(request.urlWithParams, "https://localhost:4200").searchParams).entries()
    ).map(([key, value]) => ["_" + key, value]);
    if (urlSegments.length || urlSearchParams.length) return Object.fromEntries([...urlSegments, ...urlSearchParams]) as Params;
    else return {};
  }
}
